import { useState, useEffect, SyntheticEvent } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import * as actions from '@redux-actions/';

import {
    CreditCardFormStatus,
    GeographicInformationsPayload,
    ICreditCardForm,
    AddPaymentMethodPayload,
} from '@interfaces/myProfileInterfaces';

import {
    FormControl,
    TextField,
    FormHelperText,
    Select,
    MenuItem,
    InputLabel,
    InputAdornment,
} from '@mui/material';

import NumberFormat from 'react-number-format';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import ArrowDown from '@images/svg-icons/arrowDown.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CalendarIcon from '@images/svg-icons/calendarIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import CvvTooltip from '@components/my-profile/payment-methods/CvvTooltip';

import { addPaymentMethod, getPersonalDetailsCityAndState } from '@services/myProfileServices';

import { capitalizeFirstLetter, getLabelFallback } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Controller, SubmitHandler, useForm, FieldValues } from 'react-hook-form';

import { isValid, compareAsc } from 'date-fns';

import ReCAPTCHA from 'react-google-recaptcha';

import { CARD_VALIDATION } from '@utils/const';
import { CREDIT_CARDS_LOGOS, isValidZipCode } from '@utils/pages-utils/MyProfileUtils';
import { getStringFromDate } from '@utils/dateUtils';

interface Props {
    defaultValues?: ICreditCardForm;
}

const AddCreditCardForm = ({ defaultValues }: Props): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);

    const schema = yup.object().shape({
        firstname: yup
            .string()
            .required(
                getLabelFallback('ADD_CREDITCARD_NAME')
            )
            .test(
                'invalid',
                getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_FIRSTNAME'),
                val => (val ? !/\d/.test(val) : true)
            ),
        lastname: yup
            .string()
            .required(
                getLabelFallback('ADD_CREDITCARD_NAME')
            )
            .test(
                'invalid',
                getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_LASTNAME'),
                val => (val ? !/\d/.test(val) : true)
            ),
        creditCardNumber: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_CREDITCARDNUMBER_LABEL')
                )
            )
            .test(
                'invalid',
                getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_NUMBER_ERROR_MESSAGE'),
                value => (value && !validateCreditCard(value.replace(/\s/g, '')) ? false : true)
            ),
        expirationDate: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_EXPIRATIONDATE_LABEL')
                )
            )
            .typeError(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_EXPIRATIONDATE_LABEL')
                )
            )
            .test(
                'invalid',
                getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_DATE_MESSAGE'),
                value =>
                    value && +value.substring(0, 2) <= 12 && !validateInputDate(value)
                        ? false
                        : true
            )
            .test(
                'invalid',
                getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_DATE'),
                value =>
                    value && +value.substring(0, 2) > 12 && !validateInputDate(value) ? false : true
            )
            .nullable(),
        cvv: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_CVV_LABEL')
                )
            )
            .test(
                'minLength',
                getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_CVV_ERROR'),
                val => (val.length < 3 ? false : true)
            ),
        email: yup
            .string()
            .email(getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_EMAIL_MESSAGE'))
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_EMAIL_LABEL')
                )
            ),
        address: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_ADDRESS_LABEL')
                )
            ),
        zipcode: yup
            .string()
            .test(
                'len',
                getLabelFallback(
                    `${
                        config.countryName === 'CA'
                            ? 'PAYMENT_METHODS_CREDIT_CARD_FORM_POSTAL_CODE_ERROR_MESSAGE'
                            : 'PAYMENT_METHODS_CREDIT_CARD_FORM_ZIP_CODE_ERROR_MESSAGE'
                    }`
                ),
                val => (val ? isValidZipCode(val.toString(), config) : true)
            )
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback(
                        `${
                            config.countryName === 'CA'
                                ? 'PAYMENT_METHODS_CREDIT_CARD_FORM_ZIPCODE_CA_LABEL'
                                : 'PAYMENT_METHODS_CREDIT_CARD_FORM_ZIPCODE_LABEL'
                        }`
                    ).toLowerCase()
                )
            ),
        city: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_CITY_LABEL')
                )
            ),
        state: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_STATE_LABEL')
                )
            ),
        country: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_COUNTRY_LABEL')
                )
            ),
        recaptchaResponse: yup.string(),
    });

    const {
        register,
        watch,
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        clearErrors,
        setError,
        setValue,
    } = useForm<ICreditCardForm>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues as FieldValues,
    });

    const watchCreditCardNumber = watch('creditCardNumber');
    const watchZipCode = watch('zipcode');
    const watchCity = watch('city');
    const watchExpirationDate = watch('expirationDate');

    const initialFormStatus: CreditCardFormStatus<Partial<ICreditCardForm>> = {
        firstname: {
            focus: false,
            success: false,
        },
        lastname: {
            focus: false,
            success: false,
        },
        creditCardNumber: {
            focus: false,
            success: false,
        },
        cvv: {
            focus: false,
            success: false,
        },
        email: {
            focus: false,
            success: false,
        },
        address: {
            focus: false,
            success: false,
        },
        zipcode: {
            focus: false,
            success: false,
        },
        city: {
            focus: false,
            success: false,
        },
        state: {
            focus: false,
            success: false,
        },
        country: {
            focus: false,
            success: false,
        },
        expirationDate: {
            focus: false,
            success: false,
        },
    };

    const [isOpenDatePicker, setIsOpenDatePicker] = useState<boolean>(false);
    const [creditCardBrand, setCreditCardBrand] = useState(null);
    const [creditCardType, setCreditCardType] = useState(null);
    const [formFieldStatus, setFormFieldStatus] =
        useState<CreditCardFormStatus<Partial<ICreditCardForm>>>(initialFormStatus);

    const dispatch = useDispatch();

    const addPaymentMethodRequestStatus = useSelector(
        (state: RootState) => state.myProfile.addPaymentMethodRequestStatus
    );

    const geographicInformations = useSelector(
        (state: RootState) => state.myProfile.geographicInformations
    );

    useEffect(() => {
        if (geographicInformations) {
            const { city, state } = geographicInformations;
            city ? (setValue('city', city), clearErrors('city')) : null;
            state ? (setValue('state', state), clearErrors('state')) : null;
            setFormFieldStatus({
                ...formFieldStatus,
                ...(city && { city: { success: true, focus: false } }),
                ...(state && { state: { success: true, focus: false } }),
            });
        }
        return () => {
            dispatch(actions.getGeographicInformationsSuccess(null));
        };
    }, [geographicInformations]);

    /** The belows function checks if the input date is valid */

    const validateInputDate = (inputDate: string): boolean => {
        let isValidDate = false;
        const inputFullDate = new Date(
            `${inputDate.substring(0, 2)}/01/${inputDate.substring(3, 5)}`
        );

        if (+inputDate.substring(0, 2) > 12) {
            setError('expirationDate', {
                type: 'pattern',
                message: getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_DATE'),
            });
            setFormFieldStatus({
                ...formFieldStatus,
                expirationDate: { success: false, focus: false },
            });
            return isValidDate;
        }
        compareAsc(inputFullDate, new Date('01/01/1900')) !== -1 &&
        compareAsc(inputFullDate, new Date()) === 1
            ? (clearErrors('expirationDate'),
              (isValidDate = true),
              setFormFieldStatus({
                  ...formFieldStatus,
                  expirationDate: { success: true, focus: false },
              }))
            : (setError('expirationDate', {
                  type: 'valueAsDate',
                  message: getLabelFallback(
                      'PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_DATE_MESSAGE'
                  ),
              }),
              (isValidDate = false),
              setFormFieldStatus({
                  ...formFieldStatus,
                  expirationDate: { success: false, focus: false },
              }));
        setValue('expirationDate', inputDate);
        return isValidDate;
    };

    /** The belows function checks if the datepicker dialog date is valid */
    const validateDatepickerDialogDate = (inputDate: Date): boolean => {
        let isValidDate = false;
        isValid(new Date(inputDate)) &&
        compareAsc(new Date(inputDate), new Date('01/01/1900')) !== -1 &&
        compareAsc(new Date(inputDate), new Date()) === 1
            ? (clearErrors('expirationDate'),
              (isValidDate = true),
              setFormFieldStatus({
                  ...formFieldStatus,
                  expirationDate: { success: true, focus: false },
              }))
            : (setError('expirationDate', {
                  type: 'valueAsDate',
                  message: getLabelFallback(
                      'PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_DATE_MESSAGE'
                  ),
              }),
              (isValidDate = false),
              setFormFieldStatus({
                  ...formFieldStatus,
                  expirationDate: { success: false, focus: false },
              }));
        setValue('expirationDate', getStringFromDate(inputDate, 'MM/yy'));
        return isValidDate;
    };

    const onVerifyRecaptcha = (token: string) => {
        setValue('recaptchaResponse', token);
    };

    /** The function below checks the validity of the credit card number */

    const validateCreditCard = (creditCardNumber: string) => {
        clearErrors('creditCardNumber');

        let valid: boolean = false;

        switch (true) {
            case CARD_VALIDATION.Amex.format.test(creditCardNumber):
                setCreditCardBrand('AMEX');
                setCreditCardType(CARD_VALIDATION.Amex.payMethodId);
                valid = true;
                break;
            case CARD_VALIDATION.Discover.format.test(creditCardNumber):
                setCreditCardBrand('DISCOVER');
                setCreditCardType(CARD_VALIDATION.Discover.payMethodId);
                valid = true;
                break;
            case CARD_VALIDATION.Visa.format.test(creditCardNumber):
                setCreditCardBrand('VISA');
                setCreditCardType(CARD_VALIDATION.Visa.payMethodId);
                valid = true;
                break;
            case CARD_VALIDATION.Mastercard.format.test(creditCardNumber):
                setCreditCardBrand('MASTERCARD');
                setCreditCardType(CARD_VALIDATION.Mastercard.payMethodId);
                valid = true;
                break;
            case CARD_VALIDATION.JCB.format.test(creditCardNumber):
                setCreditCardBrand('JCB');
                setCreditCardType(CARD_VALIDATION.JCB.payMethodId);
                valid = true;
                break;
            default:
                setError('creditCardNumber', {
                    type: 'pattern',
                    message: getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_NUMBER_ERROR_MESSAGE'),
                });
                break;
        }

        return valid;
    };

    /** The function below handles the onBlur, onChange and onFocus events for the form validation */
    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        !isSubmitting && clearErrors(event.name as keyof ICreditCardForm);
        if (event.name === 'zipcode') {
            if (e.type === 'change') {
                setValue(
                    event.name as keyof ICreditCardForm,
                    event.value.slice(0, event.maxLength)
                );
            }

            if (e.type === 'blur') {
                const geographicInfoPayload: GeographicInformationsPayload = {
                    isMockRequest: config.mockRequest,
                    zipCode: event.value,
                    addressType: 'other',
                };
                event.value.length < event.maxLength ||
                (config.countryName === 'CA' &&
                    !/^[A-Za-z]\d[A-Za-z][ ]\d[A-Za-z]\d$/.test(event.value))
                    ? (setFormFieldStatus({
                          ...formFieldStatus,
                          [event.name]: { success: false, focus: false },
                      }),
                      setValue(event.name, event.value),
                      setError(event.name as keyof ICreditCardForm, {
                          type: 'maxLength',
                          message: getLabelFallback(
                              `${
                                  config.countryName === 'CA'
                                      ? 'PAYMENT_METHODS_CREDIT_CARD_FORM_POSTAL_CODE_ERROR_MESSAGE'
                                      : 'PAYMENT_METHODS_CREDIT_CARD_FORM_ZIP_CODE_ERROR_MESSAGE'
                              }`
                          ),
                      }))
                    : dispatch(getPersonalDetailsCityAndState(geographicInfoPayload));
            }
        }

        if (event.name === 'email' && event.value && e.type === 'blur') {
            return validateEmail(event.value);
        }

        if (event.name === 'creditCardNumber') {
            const creditCardNumberClean = event.value.replace(/\s/g, '');
            if (e.type === 'blur') {
                validateCreditCard(creditCardNumberClean);
            }
        }

        if (event.name === 'cvv') {
            if (e.type === 'blur') {
                event.value.length < event.minLength
                    ? (setFormFieldStatus({
                          ...formFieldStatus,
                          [event.name]: { success: false, focus: false },
                      }),
                      setValue(event.name, event.value),
                      setError('cvv', {
                          type: 'minLength',
                          message: getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_CVV_ERROR'),
                      }))
                    : (setFormFieldStatus({
                          ...formFieldStatus,
                          [event.name]: { success: true, focus: false },
                      }),
                      clearErrors(event.name));
            }

            if (e.type === 'change') {
                setValue('cvv', event.value.slice(0, event.maxLength));
            }
        }

        if (event.name === 'country' && e.type === 'change') {
            setValue('country', event.value.replace(/[^a-z\s]/gi, ''));
        }

        if (event.name === 'expirationDate') {
            if (e.type === 'blur') {
                return event.value.length === 5
                    ? validateInputDate(event.value)
                    : event.value.length < 5 && event.value.length != 0
                    ? (setFormFieldStatus({
                          ...formFieldStatus,
                          expirationDate: { focus: false, success: false },
                      }),
                      setError('expirationDate', {
                          type: 'min',
                          message: getLabelFallback(
                              'PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_DATE'
                          ),
                      }))
                    : event.value.length === 0 &&
                      (setFormFieldStatus({
                          ...formFieldStatus,
                          expirationDate: { focus: false, success: false },
                      }),
                      setError('expirationDate', {
                          type: 'min',
                          message: replaceLabelPlaceholder(
                              'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                              '{FORM_FIELD}',
                              getLabelFallback(
                                  'PAYMENT_METHODS_CREDIT_CARD_FORM_EXPIRATIONDATE_FIELD_LABEL'
                              )
                          ),
                      }));
            }
        }

        if (
            ['firstname', 'lastname', 'address', 'city'].includes(event.name) &&
            e.type == 'blur' &&
            event.value.length &&
            event.value.trim() === ''
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [event.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(event.name as keyof ICreditCardForm, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `ADDRESSES_ADDRESS_FORM_INVALID_${event.name.toUpperCase()}`
                    ),
                })
            );
        }

        if (
            ['firstname', 'lastname'].includes(event.name) &&
            e.type == 'blur' &&
            event.value.length &&
            /\d/.test(event.value)
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [event.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(event.name as keyof ICreditCardForm, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_${event.name.toUpperCase()}`
                    ),
                })
            );
        }
        event.value
            ? (setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: {
                      success: e.type === 'blur' ? true : e.type === 'change' ? false : false,
                      focus:
                          e.type === 'blur'
                              ? false
                              : e.type === 'change'
                              ? true
                              : e.type === 'focus' && !errors[event.name]
                              ? true
                              : false,
                  },
              }),
              setValue(event.name as keyof ICreditCardForm, event.value))
            : (setValue(event.name as keyof ICreditCardForm, ''),
              setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: { success: false, focus: false },
              }),
              e.type === 'blur' &&
                  setError(event.name as keyof ICreditCardForm, {
                      type: 'required',
                      message: replaceLabelPlaceholder(
                          'PAYMENT_METHODS_CREDIT_CARD_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                          '{FORM_FIELD}',
                          getLabelFallback(
                              `PAYMENT_METHODS_CREDIT_CARD_FORM_${event.name.toLocaleUpperCase()}_LABEL`
                          )
                      ),
                  }));
    };

    /** The function below validates user email */

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        emailRegex.test(email)
            ? (clearErrors('email'),
              setFormFieldStatus({
                  ...formFieldStatus,
                  email: {
                      success: true,
                      focus: false,
                  },
              }))
            : (setError('email', {
                  type: 'pattern',
                  message: getLabelFallback(
                      'PAYMENT_METHODS_CREDIT_CARD_FORM_INVALID_EMAIL_MESSAGE'
                  ),
              }),
              setFormFieldStatus({
                  ...formFieldStatus,
                  email: {
                      success: false,
                      focus: false,
                  },
              }));
        setValue('email', email);
    };

    /** The function below returns the input date in MM/yy format */

    const getFormattedExpirationDate = (input: string): string => {
        let clearDate = input.replace(/\//g, '');
        let formattedDate = '';

        if (clearDate.length === 1) {
            formattedDate = clearDate;
        }
        if (clearDate.length >= 2 && clearDate.length < 4) {
            formattedDate = clearDate.slice(0, 2) + '/' + clearDate.slice(2, 3);
        }
        if (clearDate.length >= 4) {
            formattedDate = clearDate.slice(0, 2) + '/' + clearDate.slice(2, 4);
        }

        return formattedDate;
    };

    const getRecaptchaResponse = (data: ICreditCardForm) => {
        if (!!data.recaptchaResponse) {
            return {
                recaptchaResponse: data.recaptchaResponse,
            };
        } else {
            return {};
        }
    };

    const onSubmit: SubmitHandler<ICreditCardForm> = (data: ICreditCardForm) => {
        const dataToSend: AddPaymentMethodPayload = {
            billTo_city: data.city,
            billTo_country: data.country,
            billTo_email: data.email,
            billTo_firstName: data.firstname,
            billTo_lastName: data.lastname,
            billTo_postalCode: data.zipcode,
            billTo_state: data.state,
            billTo_street1: data.address,
            card_accountNumber: data.creditCardNumber.replace(/\s/g, ''),
            card_cardType: creditCardType,
            card_expirationMonth: data.expirationDate.substring(0, 2),
            card_expirationYear: String(
                new Date(
                    `${data.expirationDate.substring(0, 2)}/01/${data.expirationDate.substring(
                        3,
                        5
                    )}`
                ).getFullYear()
            ),
            cc_cvc: data.cvv,
            ...getRecaptchaResponse(data),
        };

        dispatch(addPaymentMethod(config.mockRequest, config.storeId, dataToSend));
    };

    return (
        <div className="content-container credit-card-details-form">
            <span
                className="credit-card-details-form-title"
                role="region"
                aria-label="credit card form title"
            >
                {getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_TITLE')}
            </span>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group form-group_name">
                    <TextField
                        {...register('firstname')}
                        className={`${
                            errors?.firstname?.message
                                ? 'form-field-error'
                                : formFieldStatus.firstname.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="firstname"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_FIRSTNAME_FIELD_LABEL')
                        )}
                        helperText={
                            errors?.firstname?.message ||
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_NAME_AS_SHOWN')
                        }
                        InputProps={{
                            endAdornment: formFieldStatus.firstname.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('firstname', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.firstname?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.firstname.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <TextField
                        {...register('lastname')}
                        className={`${
                            errors?.lastname?.message
                                ? 'form-field-error'
                                : formFieldStatus.lastname.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="lastname"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_LASTNAME_FIELD_LABEL')
                        )}
                        helperText={
                            errors?.lastname?.message ||
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_NAME_AS_SHOWN')
                        }
                        InputProps={{
                            endAdornment: formFieldStatus.lastname.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('lastname', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.lastname?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.lastname.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
                <div className="form-group form-group_card-number">
                    <NumberFormat
                        {...register('creditCardNumber')}
                        format={'#### #### #### ####'}
                        customInput={TextField}
                        className={`${
                            errors?.creditCardNumber?.message
                                ? 'form-field-error'
                                : formFieldStatus.creditCardNumber.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        name="creditCardNumber"
                        variant="outlined"
                        value={watchCreditCardNumber}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        defaultValue={watchCreditCardNumber}
                        label={capitalizeFirstLetter(
                            getLabelFallback(
                                'PAYMENT_METHODS_CREDIT_CARD_FORM_CREDITCARDNUMBER_FIELD_LABEL'
                            )
                        )}
                        helperText={errors?.creditCardNumber?.message}
                        FormHelperTextProps={{
                            classes: {
                                root: 'full-width',
                            },
                        }}
                        InputProps={{
                            endAdornment: formFieldStatus.creditCardNumber.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('creditCardNumber', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.creditCardNumber?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.creditCardNumber.success ? (
                                <InputAdornment
                                    className={`${
                                        CREDIT_CARDS_LOGOS[creditCardBrand] ? 'large' : ''
                                    }`}
                                    position="end"
                                >
                                    <SuccessIcon />
                                    {CREDIT_CARDS_LOGOS[creditCardBrand]}
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
                <div className="form-group form-group_expiration-date">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                            {...register('expirationDate')}
                            minDate={new Date()}
                            disablePast
                            className={`${
                                errors?.expirationDate?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.expirationDate.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            label={capitalizeFirstLetter(
                                getLabelFallback(
                                    'PAYMENT_METHODS_CREDIT_CARD_FORM_EXPIRATIONDATE_FIELD_LABEL'
                                )
                            )}
                            open={isOpenDatePicker}
                            onAccept={date => validateDatepickerDialogDate(date)}
                            onClose={() => setIsOpenDatePicker(false)}
                            openTo="year"
                            views={['year', 'month']}
                            inputFormat="MM/yy"
                            mask="__/__"
                            disableMaskedInput={false}
                            value={watchExpirationDate ?? ''}
                            onChange={() => {}}
                            renderInput={params => (
                                <TextField
                                    value={watchExpirationDate ?? ''}
                                    helperText={errors?.expirationDate?.message}
                                    name="expirationDate"
                                    variant="outlined"
                                    onBlur={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onChange={(e: SyntheticEvent) => (
                                        setFormFieldStatus({
                                            ...formFieldStatus,
                                            expirationDate: { success: false, focus: true },
                                        }),
                                        setValue(
                                            'expirationDate',
                                            /^[\/0-9\b]+$/.test(
                                                (e.target as HTMLInputElement).value
                                            )
                                                ? getFormattedExpirationDate(
                                                      (e.target as HTMLInputElement).value
                                                  )
                                                : null
                                        ),
                                        clearErrors('expirationDate')
                                    )}
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        value: watchExpirationDate ?? '',
                                    }}
                                    InputProps={{
                                        endAdornment: formFieldStatus.expirationDate.focus ? (
                                            <InputAdornment className="large" position="end">
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() =>
                                                        setValue('expirationDate', '')
                                                    }
                                                />
                                                <CalendarIcon
                                                    className="input-field-icon"
                                                    onClick={() => setIsOpenDatePicker(true)}
                                                />
                                            </InputAdornment>
                                        ) : errors?.expirationDate?.message ? (
                                            <InputAdornment className="large" position="end">
                                                <ErrorIcon />
                                                <CalendarIcon
                                                    className="input-field-icon"
                                                    onClick={() => setIsOpenDatePicker(true)}
                                                />
                                            </InputAdornment>
                                        ) : formFieldStatus.expirationDate.success ? (
                                            <InputAdornment className="large" position="end">
                                                <SuccessIcon />
                                                <CalendarIcon
                                                    className="input-field-icon"
                                                    onClick={() => setIsOpenDatePicker(true)}
                                                />
                                            </InputAdornment>
                                        ) : (
                                            <CalendarIcon
                                                className="input-field-icon medium"
                                                onClick={() => setIsOpenDatePicker(true)}
                                            />
                                        ),
                                    }}
                                    error={Boolean(errors?.expirationDate?.message)}
                                />
                            )}
                        />
                    </LocalizationProvider>
                    <div className={`cvv 
                            ${
                                errors?.cvv?.message
                                    ? 'form-field-error'
                                    : ''
                            }`}>
                        <TextField
                            {...register('cvv')}
                            className={`${
                                errors?.cvv?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.cvv.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            sx={{ width: '100%' }}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="cvv"
                            variant="outlined"
                            label={getLabelFallback(
                                'PAYMENT_METHODS_CREDIT_CARD_FORM_CVV_FIELD_LABEL'
                            ).toUpperCase()}
                            helperText={errors?.cvv?.message}
                            inputProps={{
                                minLength: 3,
                                maxLength: 4,
                            }}
                            InputProps={{
                                endAdornment: formFieldStatus.cvv.focus ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('cvv', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.cvv?.message ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formFieldStatus.cvv.success ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <CvvTooltip />
                    </div>
                </div>
                <div className="form-group form-group_email">
                    <TextField
                        {...register('email')}
                        className={`${
                            errors?.email?.message
                                ? 'form-field-error'
                                : formFieldStatus.email.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="email"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_EMAIL_FIELD_LABEL')
                        )}
                        helperText={errors?.email?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.email.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('email', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.email?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.email.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
                <div className="form-group form-group_address">
                    <TextField
                        {...register('address')}
                        className={`${
                            errors?.address?.message
                                ? 'form-field-error'
                                : formFieldStatus.address.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="address"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_ADDRESS_FIELD_LABEL')
                        )}
                        helperText={errors?.address?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.address.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('address', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.address?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.address.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
                <div className="form-group form-group_zip-code">
                    <TextField
                        {...register('zipcode')}
                        className={`medium-input-field ${
                            errors?.zipcode?.message
                                ? 'form-field-error'
                                : formFieldStatus.zipcode.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="zipcode"
                        variant="outlined"
                        type={`${config.countryName.toUpperCase() === 'CA' ? 'text' : 'number'}`}
                        label={getLabelFallback(
                            `${
                                config.countryName === 'CA'
                                    ? 'PAYMENT_METHODS_CREDIT_CARD_FORM_ZIPCODE_CA_FIELD_LABEL'
                                    : 'PAYMENT_METHODS_CREDIT_CARD_FORM_ZIPCODE_FIELD_LABEL'
                            }`
                        )}
                        defaultValue={watchZipCode || null}
                        helperText={errors?.zipcode?.message}
                        inputProps={{
                            minLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5}`,
                            maxLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5}`,
                        }}
                        InputProps={{
                            endAdornment: formFieldStatus.zipcode.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('zipcode', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.zipcode?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.zipcode.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
                <div className="form-group form-group_city">
                    <TextField
                        {...register('city')}
                        className={`${
                            errors?.city?.message
                                ? 'form-field-error'
                                : formFieldStatus.city.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="city"
                        variant="outlined"
                        value={watchCity || ''}
                        label={capitalizeFirstLetter(
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_CITY_FIELD_LABEL')
                        )}
                        helperText={errors?.city?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.city.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('city', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.city?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.city.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Controller
                        name="state"
                        control={control}
                        defaultValue=""
                        render={({ field: { value }, fieldState, field }) => (
                            <FormControl
                                className={` ${
                                    fieldState?.error?.message
                                        ? 'form-field-error'
                                        : value
                                        ? 'form-field-success'
                                        : ''
                                }`}
                            >
                                <InputLabel id="billing-state-label">
                                    {capitalizeFirstLetter(
                                        getLabelFallback(
                                            'PAYMENT_METHODS_CREDIT_CARD_FORM_STATE_FIELD_LABEL'
                                        )
                                    )}
                                </InputLabel>
                                <Select
                                    name="state"
                                    labelId="billing-state-label"
                                    variant="outlined"
                                    IconComponent={props => <ArrowDown {...props} />}
                                    label={capitalizeFirstLetter(
                                        getLabelFallback(
                                            'PAYMENT_METHODS_CREDIT_CARD_FORM_STATE_FIELD_LABEL'
                                        )
                                    )}
                                    endAdornment={
                                        fieldState?.error?.message ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    width: '25px',
                                                    position: 'absolute',
                                                    right: '45px',
                                                }}
                                            >
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : value ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    width: '25px',
                                                    position: 'absolute',
                                                    right: '45px',
                                                }}
                                            >
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null
                                    }
                                    {...field}
                                >
                                    {config.stateList.map(option => (
                                        <MenuItem
                                            key={option.code}
                                            value={option.code}
                                            disabled={option.disabled}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors?.state?.message && (
                                    <FormHelperText className="error-helper-text">
                                        {errors?.state?.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                </div>
                <div className="form-group form-group_country">
                    <TextField
                        {...register('country')}
                        className={`${
                            errors?.country?.message
                                ? 'form-field-error'
                                : formFieldStatus.country.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="country"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_COUNTRY_FIELD_LABEL')
                        )}
                        helperText={errors?.country?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.country.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('country', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.country?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.country.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
                <div className="external-informations">
                    {!!config &&
                        !!config.clientKeyRecaptcha &&
                        config.clientKeyRecaptcha.length > 0 && (
                            <ReCAPTCHA
                                {...register('recaptchaResponse')}
                                sitekey={config.clientKeyRecaptcha}
                                hl={config.country}
                                onChange={onVerifyRecaptcha}
                            />
                        )}
                    {addPaymentMethodRequestStatus === 'ERROR' && (
                        <div className="primary-message small error align-left error-message">
                            {getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_ERROR_MESSAGE')}
                        </div>
                    )}
                </div>
                <div>
                    <button
                        data-element-id="PaymentMethods_Add"
                        className={`primary-button button-size-medium ${addPaymentMethodRequestStatus === 'LOADING' ? 'loading' : ''
                            }`}
                        type="submit"
                    >
                        {getLabelFallback('PAYMENT_METHODS_CREDIT_CARD_FORM_SUBMIT_CTA_LABEL')}
                        {addPaymentMethodRequestStatus === 'LOADING' && <PreLoader />}
                    </button>
                </div>
            </form>
        </div>
    );
};
export default AddCreditCardForm;
